import * as ex from "excalibur";
import { CONFIG } from "../config";
import { MAP_CENTER, MAX_RADIUS, MAP_GRID_SIZE } from "@dein-go-city/shared";
import { createTapHandler } from "../input/tap";
export class CityMap {
    isoMap;
    onTileClick = null;
    // Tiles occupied by buildings — blocked for placement, checked by GameScene.ts's
    // isOccupiedOrStreet for both special-building and company placement today.
    _occupied = new Set();
    constructor() {
        this.isoMap = new ex.IsometricMap({
            pos: ex.vec(0, 0),
            tileWidth: CONFIG.TILE_WIDTH,
            tileHeight: CONFIG.TILE_HEIGHT,
            columns: MAP_GRID_SIZE,
            rows: MAP_GRID_SIZE,
            renderFromTopOfGraphic: true,
        });
        this.initTiles();
    }
    // Grid-coord key — purely internal, matches companies.tileX/tileY format.
    static tileKey(tile) {
        return `${tile.x},${tile.y}`;
    }
    initTiles() {
        for (const tile of this.isoMap.tiles) {
            // Pre-bake z and remove IsometricEntitySystem overhead for every tile.
            // Visuals come from TileChunkManager (BackgroundCanvas.ts) — no graphics added here.
            const transform = tile.get(ex.TransformComponent);
            transform.z = transform.pos.y;
            tile.removeComponent(ex.IsometricEntityComponent);
        }
    }
    // One global pointer listener + getTileByPoint instead of a tap handler on each of the
    // ~8,281 buildable tiles — mirrors the hover-cursor preview in GameScene.ts's setupBuildMode.
    // Only called for non-read-only sessions (see GameScene.onInitialize), which is the only case
    // that ever needs tile-tap placement.
    attachClickHandling(engine) {
        const tap = createTapHandler((evt) => {
            const tile = this.isoMap.getTileByPoint(evt.worldPos);
            if (tile && this.isTileBuildable(tile))
                this.onTileClick?.(tile);
        });
        engine.input.pointers.on("down", tap.down);
        engine.input.pointers.on("up", tap.up);
        engine.input.pointers.on("cancel", tap.cancel);
    }
    // Remove all tile entities from ECS systems. Pointer events still work (see above).
    // Call after scene.add(isoMap) so tiles are in the scene before being killed.
    killAllTiles() {
        for (const tile of this.isoMap.tiles)
            tile.kill();
    }
    // A tile is "buildable" if it falls within the maximum possible map extent.
    // Gates attachClickHandling's tap dispatch during build mode (special buildings today;
    // decoration-placement mode, if it ships, would reuse this same gate).
    isTileBuildable(tile) {
        return (Math.abs(tile.x - MAP_CENTER) <= MAX_RADIUS &&
            Math.abs(tile.y - MAP_CENTER) <= MAX_RADIUS);
    }
    isTileOccupied(tile) {
        return this._occupied.has(CityMap.tileKey(tile));
    }
    occupyTile(tile) {
        this._occupied.add(CityMap.tileKey(tile));
    }
    releaseTile(tile) {
        this._occupied.delete(CityMap.tileKey(tile));
    }
    // Registers the tile-tap handler used by build mode today (see GameScene.ts's
    // setupBuildMode); decoration-placement mode, if it ships, would reuse this same hook.
    setOnTileClick(handler) {
        this.onTileClick = handler;
    }
    getWorldCenter() {
        return this.isoMap.getTile(MAP_CENTER, MAP_CENTER)?.center ?? ex.vec(0, 0);
    }
}
